import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { ChangePasswordRequest, ChangePasswordResponse } from '@nadoutong/contracts';
import { changePassword as apiChangePassword } from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { Disclosure } from '../components/Disclosure.tsx';
import { useToast } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Chip, FormField, Input, PageHeader, Panel } from '../ui/index.ts';

/**
 * `/settings(/account|/security|/technical)` — read-only account info +
 * in-session password change + technical ids (epic #897 PR 9, local
 * Dispatch Deck rebuild of the console-shared factory page).
 *
 * Team governance does NOT live here — members and invitations belong
 * to `/team/members` (epic #897 PR 7); this page neither queries nor
 * renders any of it.
 *
 * Password change (R5 / PR-E.2) calls `POST /v1/auth/password/change`
 * with the caller's current + new passwords. The endpoint re-verifies
 * the current password server-side; a bad match renders the 401
 * envelope as an inline "current password incorrect" field error,
 * keeping UI messaging consistent with /login. Length is validated
 * locally and on the server (10–128 chars). Success reports through
 * the toast and resets the form (single-channel: no inline duplicate).
 *
 * The `/settings/:section` sub-paths keep their scroll-anchor
 * behaviour: each section carries `id="settings-section-<name>"` and
 * mount scrolls the requested one into view.
 */

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

export type SettingsSection = 'account' | 'security' | 'technical';

export interface SettingsProps {
  readonly initialSection?: SettingsSection;
}

export function Settings({ initialSection = 'account' }: SettingsProps): JSX.Element {
  const t = useT();
  const { user } = useSession();

  useEffect(() => {
    const target = document.getElementById(`settings-section-${initialSection}`);
    target?.scrollIntoView({ block: 'start' });
  }, [initialSection]);

  return (
    <section data-testid="settings-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('settings.title')}
        sub={t('settings.subtitle')}
        testId="settings-header"
      />

      <div id="settings-section-account" className="scroll-mt-8">
        <Panel caption={t('settings.account.title')} padded testId="settings-account-card">
          <div className="flex flex-col gap-4">
            <p className="m-0 text-dense text-paper-low">{t('settings.account.description')}</p>
            <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <dt className="text-label text-paper-low">{t('settings.account.email')}</dt>
                <dd
                  className="m-0 font-mono text-dense text-paper"
                  data-testid="settings-account-email"
                >
                  {user?.email ?? '—'}
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="text-label text-paper-low">
                  {t('settings.account.email_verified')}
                </dt>
                <dd className="m-0" data-testid="settings-email-verified">
                  {user?.emailVerified === true ? (
                    <Chip tone="ok">{t('settings.account.status.verified')}</Chip>
                  ) : (
                    <Chip tone="warn">{t('settings.account.status.unverified')}</Chip>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </Panel>
      </div>

      <div id="settings-section-security" className="scroll-mt-8">
        <ChangePasswordPanel />
      </div>

      <div id="settings-section-technical" className="scroll-mt-8">
        <Disclosure
          title={t('common.technical_details.title')}
          summary={t('settings.technical.summary')}
          showLabel={t('common.technical_details.show')}
          hideLabel={t('common.technical_details.hide')}
          testId="settings-technical-card"
        >
          <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <dt className="text-label text-paper-low">{t('settings.account.account_id')}</dt>
              <dd
                className="m-0 font-mono text-dense text-paper-mid"
                data-testid="settings-account-id"
              >
                {user?.accountId ?? '—'}
              </dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="text-label text-paper-low">{t('settings.account.org_id')}</dt>
              <dd className="m-0 font-mono text-dense text-paper-mid" data-testid="settings-org-id">
                {user?.orgId ?? '—'}
              </dd>
            </div>
          </dl>
        </Disclosure>
      </div>
    </section>
  );
}

function useChangePasswordMutation(): UseMutationResult<
  ChangePasswordResponse,
  Error,
  ChangePasswordRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body) => apiChangePassword(body, fetchImpl),
  });
}

interface FieldErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

function ChangePasswordPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useChangePasswordMutation();
  const submitting = mutation.isPending;

  function resetForm(): void {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);

    const nextErrors: FieldErrors = {};
    if (currentPassword.length === 0) {
      nextErrors.currentPassword = t('settings.security.error.current_required');
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_short', {
        min: String(MIN_PASSWORD_LENGTH),
      });
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_long', {
        max: String(MAX_PASSWORD_LENGTH),
      });
    }
    if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('settings.security.error.mismatch');
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    try {
      await mutation.mutateAsync({ currentPassword, newPassword });
      resetForm();
      toast.success(t('settings.security.toast.success'));
    } catch (err) {
      if (err instanceof PlatformApiError) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setFieldErrors({
            currentPassword: t('settings.security.error.current_incorrect'),
          });
          return;
        }
        if (err.code === 'INVALID_PAYLOAD') {
          setFieldErrors({ newPassword: err.message });
          return;
        }
        setFormError(`${err.code}: ${err.message}`);
        return;
      }
      setFormError(t('settings.security.error.network'));
    }
  }

  return (
    <Panel caption={t('settings.security.title')} padded testId="settings-security-card">
      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        data-testid="settings-change-password-form"
        noValidate
      >
        <p className="m-0 text-dense text-paper-mid">{t('settings.security.change_password')}</p>

        <FormField
          label={t('settings.security.current_password')}
          fieldId="settings-current-password"
          required
          error={
            fieldErrors.currentPassword !== undefined ? (
              <span data-testid="settings-current-password-error">
                {fieldErrors.currentPassword}
              </span>
            ) : undefined
          }
        >
          <Input
            id="settings-current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            disabled={submitting}
            invalid={fieldErrors.currentPassword !== undefined}
            aria-describedby={
              fieldErrors.currentPassword !== undefined
                ? 'settings-current-password-error'
                : undefined
            }
            data-testid="settings-current-password"
          />
        </FormField>

        <FormField
          label={t('settings.security.new_password')}
          fieldId="settings-new-password"
          required
          error={
            fieldErrors.newPassword !== undefined ? (
              <span data-testid="settings-new-password-error">{fieldErrors.newPassword}</span>
            ) : undefined
          }
        >
          <Input
            id="settings-new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            disabled={submitting}
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            invalid={fieldErrors.newPassword !== undefined}
            aria-describedby={
              fieldErrors.newPassword !== undefined ? 'settings-new-password-error' : undefined
            }
            data-testid="settings-new-password"
          />
        </FormField>

        <FormField
          label={t('settings.security.confirm_password')}
          fieldId="settings-confirm-password"
          required
          error={
            fieldErrors.confirmPassword !== undefined ? (
              <span data-testid="settings-confirm-password-error">
                {fieldErrors.confirmPassword}
              </span>
            ) : undefined
          }
        >
          <Input
            id="settings-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            disabled={submitting}
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            invalid={fieldErrors.confirmPassword !== undefined}
            aria-describedby={
              fieldErrors.confirmPassword !== undefined
                ? 'settings-confirm-password-error'
                : undefined
            }
            data-testid="settings-confirm-password"
          />
        </FormField>

        {formError !== null ? (
          <p
            role="alert"
            className="m-0 text-label text-bad"
            data-testid="settings-change-password-error"
          >
            {formError}
          </p>
        ) : null}

        <div className="flex items-center justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="settings-change-password-submit"
          >
            {t('settings.security.submit')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
